import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, NavLink, useLocation } from 'react-router-dom';

import { useAppContext } from 'services/context';
import { USER_ACTIONS } from 'services/reducers/actionTypes';
import { useTranslation } from 'services/hooks';
import { ROUTES, ROLES, COURSE_TYPES, COURSE_IDS } from 'constants/index.js';

import Button from 'components/Button';
import Icon from 'components/Icon';
import LanguageSelector from 'components/LanguageSelector';

import './style.css';

const SWIPE_THRESHOLD = 50;

const Header = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const getTranslation = useTranslation();

	const TRANSLATION_KEY = 'header';

	const { user, dispatchUser, Api, setResponseMessage, courses, ddls } = useAppContext();

	const [open, setOpen] = useState(false);
	const [links, setLinks] = useState();

	const toggleMenu = () => {
		if (!user?.token) return;
		setOpen(prev => !prev);
	};

	const _handleLogOut = () => {
		dispatchUser({ type: USER_ACTIONS.LOG_OUT });
		Api.Logout();
		navigate(ROUTES.LOGIN);
		setOpen(false);
	};

	const allowedLinksByRole = _links => {
		//exclude Conversation for students that not belong to IELTS OR ESL
		if (user.token && user.roleId === ROLES.Student) {
			const hasConversation = courses?.courses?.some(
				course =>
					(course.courseTypeId === COURSE_TYPES.IELTS || course.courseTypeId === COURSE_TYPES.ESL) &&
					course.courseTypeId !== COURSE_TYPES.IELTS_GENERAL &&
					!course.disabled
			);
			if (!hasConversation) {
				const newLinks = _links?.filter(link => link.linkId !== 17); //Conversations
				return newLinks;
			}
		}

		//exclude classes Management for ESL teachers (NOT IELTS)
		if (user.token && (user.roleId === ROLES.Teacher || user.roleId === ROLES.TeacherTeamLeader)) {
			const _courseTeachers = ddls?.courseTeachers;
			if (!_courseTeachers) return;
			const currentTeacherCoursesList = _courseTeachers?.options.filter(({ value }) => {
				return value === user.userId;
			});
			if (!currentTeacherCoursesList) return;
			const isIELTSTeacher = currentTeacherCoursesList.some(course => {
				return course.courseId === COURSE_IDS.IELTS;
			});
			if (!isIELTSTeacher) {
				const newLinks = _links?.filter(link => link.linkId !== 13); //Classes management
				return newLinks;
			}
		}
		return _links;
	};

	//fix showing loader from management when page changed
	useEffect(() => {
		if (location && location.pathname !== ROUTES.MANAGEMENT_PANEL) setResponseMessage({ visible: false, message: '', failed: false });
	}, [location]);

	useEffect(() => {
		if (!user.token) return;
		(async () => {
			let _links = await Api.GetDynamicLinks(1, user.roleId);
			_links = _links.sort((a, b) => (a.linkId > b.linkId ? 1 : -1));
			if (_links && Array.isArray(_links)) {
				_links = allowedLinksByRole(_links);
				setLinks(_links);
			} else {
				console.warn('no header links falling to fallback');
				setLinks([
					{
						linkDisplayName: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_personal_area_link_title`),
						linkURL: ROUTES.PERSONAL_AREA,
					},
					{ linkDisplayName: getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_home_link_title`), linkURL: ROUTES.HOME },
				]);
			}
		})();
	}, [user?.token, ddls, courses, getTranslation]);

	const _handleLogIn = () => {
		if (location && location.pathname !== ROUTES.LOGIN) navigate(ROUTES.LOGIN);
	};

	let touchStart = 0;

	const _handleTouchStart = e => (touchStart = e?.touches[0]?.screenX);

	const _handleTouchEnd = e => {
		try {
			let touchEnd = e?.changedTouches[0]?.screenX;
			const deltaX = Math.abs(touchStart - touchEnd);
			if (touchStart < touchEnd && deltaX > SWIPE_THRESHOLD) setOpen(false); //only on swipe right more than 50 pixels
			touchStart = 0;
		} catch (err) {}
	};

	const isUserLogin = useMemo(() => {
		return user && user.token;
	}, [user]);

	//render role value for non students roles
	const buttonTitle = user.roleId !== ROLES.Student ? `${user.fname} - ${ROLES[user.roleId]}` : user.fname;

	const _renderLogInButton =
		user && user.token ? (
			<Button
				title={buttonTitle}
				startIcon="userIcon"
				iconSize={25}
				endIconIconSize={15}
				endIcon="dropdownArrow"
				skew
				onClick={toggleMenu}
			/>
		) : (
			<Button
				title={getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_log_in_link_title`)}
				endIcon="rightArrow"
				iconSize={15}
				skew
				onClick={_handleLogIn}
			/>
		);
	return (
		<div className="Header">
			<a href={ROUTES.HOME} target="_blank">
				<div className="logo"></div>
			</a>
			<div className="right_side">
				{isUserLogin && <LanguageSelector />}
				<div className={`links_wrapper ${isUserLogin ? 'lang' : ''}`}>
					{_renderLogInButton}
					<ul className={`links ${open ? 'open' : 'close'}`} onTouchStart={_handleTouchStart} onTouchEnd={_handleTouchEnd}>
						{links?.map(({ linkURL, linkDisplayName, isLinkNewWindow, linkId }, idx) => (
							<li key={idx} onClick={toggleMenu}>
								{!isLinkNewWindow && (
									<NavLink to={linkURL} target={isLinkNewWindow ? '_blank' : ''}>
										{linkDisplayName}
										{linkId === 17 && user.unreadMessagesCount > 0 && <span className="unReadMessages">{user.unreadMessagesCount}</span>}
									</NavLink>
								)}
								{isLinkNewWindow && (
									<a href={ROUTES.HOME} target="_blank">
										{linkDisplayName}
									</a>
								)}
							</li>
						))}

						{user?.token && (
							<li onClick={_handleLogOut} className="log_out">
								{getTranslation(TRANSLATION_KEY, `${TRANSLATION_KEY}_log_out_link_title`)}
								<Icon icon="rightArrow" size={20} className="logout_icon" />
							</li>
						)}
					</ul>
				</div>
				<div className={`burger ${open ? 'open' : ''}`} onClick={toggleMenu}>
					<span className="line"></span>
					<span className="line"></span>
					<span className="line"></span>
				</div>
			</div>
		</div>
	);
};

export default Header;
